import {
  ActionIcon,
  Button,
  CloseButton,
  Menu,
  Pagination,
  Select,
  Skeleton,
  Tabs,
  TextInput,
  UnstyledButton,
} from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import {
  IconAlertTriangle,
  IconAntenna,
  IconCertificateOff,
  IconChevronDown,
  IconChevronRight,
  IconChevronUp,
  IconCircleCheck,
  IconCircleDashed,
  IconCircleX,
  IconCloudDownload,
  IconDotsVertical,
  IconFileCertificate,
  IconFileDescription,
  IconFileOff,
  IconFileText,
  IconFileX,
  IconFilterOff,
  IconFirstAidKit,
  IconFlame,
  IconHourglass,
  IconId,
  IconInfoCircleFilled,
  IconPencil,
  IconPlus,
  IconSearch,
  IconSelector,
  IconShieldCheck,
  IconTrash,
  IconUpload,
  IconUserCircle,
  IconUserFilled,
  IconUserSearch,
  IconUsers,
  type TablerIcon,
} from '@tabler/icons-react';
import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '../../EmptyState.js';
import { formatDay } from '../../format.js';
import { PageHeader } from '../../PageHeader.js';
import { Panel } from '../../Panel.js';
import { PersonAvatar } from '../../PersonAvatar.js';
import { StatusBadge, type StatusTone } from '../../StatusBadge.js';
import { useShell } from '../../shell-context.js';
import { ShipGlyph } from '../../VesselBadge.js';
import { CertificateOverview } from '../Overview.js';
import classes from './CertificatesScreen.module.css';
import { CREW_FILE_ACCEPT, CrewFileViewer, crewFileErrorKey } from './CrewFileViewer.js';
import { queryUser } from './CrewListScreen.js';
import {
  CERTIFICATE_CATEGORIES,
  CERTIFICATE_PAGE_SIZE,
  type CertificateCategory,
  type CertificateRow,
  type CertificateSort,
  type CertificateSortKey,
  categoryCounts,
  certificateRows,
  crewOfGroup,
  daysLeft,
  type RowStatus,
  type StatusFilter,
  vesselGroups,
} from './certificates.js';
import { pageOf } from './crew.js';
import { errorCode, type PersonnelSource } from './source.js';
import type {
  CertificateRecord,
  CrewCertificates,
  CrewFileRecord,
  CrewMember,
  PersonnelVessel,
} from './types.js';

/** "Yakında dolacak" eşiği (gün; ADR 0016 §7, ADR 0018 §2). Sunucudaki varsayılanla aynı. */
const EXPIRING_DAYS = 90;
/** Sol listede ilk gösterilen kişi sayısı; fazlası "+ N more crew" ile açılır (tasarımdaki gibi). */
const CREW_PREVIEW = 8;
/** Sağ paneldeki "Certificate Details" kartı sayısı. */
const DETAILS_PREVIEW = 6;
/** Yükleniyor iskeletindeki satır sayısı. */
const SKELETON_ROWS = 6;

const TONE: Record<RowStatus, StatusTone> = {
  valid: 'success',
  expiring: 'warning',
  expired: 'danger',
  missing: 'neutral',
};

const STATUS_ICON: Record<RowStatus, TablerIcon> = {
  valid: IconCircleCheck,
  expiring: IconAlertTriangle,
  expired: IconCircleX,
  missing: IconCircleDashed,
};

const CATEGORY_ICON: Record<CertificateCategory, TablerIcon> = {
  competency: IconFileCertificate,
  gmdss: IconAntenna,
  stcw_safety: IconFlame,
  security: IconShieldCheck,
  medical: IconFirstAidKit,
  specialized: IconFileDescription,
  personal: IconId,
  other: IconCircleDashed,
};

type Base =
  | { kind: 'loading' }
  | { kind: 'error'; code: string }
  | { kind: 'ready'; today: string; vessels: PersonnelVessel[]; crew: CrewMember[] };

type Detail =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'error'; code: string }
  | { kind: 'ready'; data: CrewCertificates };

export interface CertificateActions {
  add(person: CrewMember, presetTypeId: string | null): void;
  edit(person: CrewMember, record: CertificateRecord): void;
  remove(person: CrewMember, record: CertificateRecord): void;
  /**
   * Belgeye dosya ya da yeni sürüm yükler (ADR 0164): PDF, JPG, PNG, en çok 10 MB; eskisi silinmez.
   * Verilmezse menüde yükleme yok.
   */
  uploadFile?(person: CrewMember, record: CertificateRecord, file: File): Promise<void>;
}

export interface CertificatesScreenProps {
  source: PersonnelSource;
  /** Belge yazma yalnız ofiste (ADR 0016 §8); verilmezse ekran salt okunur (gemi uygulaması). */
  actions?: CertificateActions | undefined;
  /** Değişince seçili kişinin belgeleri yeniden okunur (kayıttan sonra). */
  reloadKey?: number | undefined;
}

function categoryOf(category: string): CertificateCategory {
  return (CERTIFICATE_CATEGORIES as readonly string[]).includes(category)
    ? (category as CertificateCategory)
    : 'other';
}

/**
 * Sertifikalar ve belgeler (müşteri ekranı `docs/ekranlar/personel-sertifikalar.jpeg`; ADR 0016
 * §7, ADR 0018 §4, ADR 0019): süzgeç kartı, gemiler ve personel, kişi başlığı ve sayıları, belge
 * matrisi, özet ve kategoriler; sağda kişi ayrıntıları. Gemi uygulaması ve web ofis aynı ekranı
 * kullanır; veri kaynağı farklıdır. `?user=<kimlik>` ile gelince o kişi seçilir (ADR 0033 eki).
 */
export function CertificatesScreen({
  source,
  actions: writeActions,
  reloadKey = 0,
}: CertificatesScreenProps) {
  const { t, i18n } = useTranslation();
  const shell = useShell();
  const locale = i18n.resolvedLanguage ?? 'en';
  const focusUser = queryUser(shell?.search);
  const [base, setBase] = useState<Base>({ kind: 'loading' });
  const [detail, setDetail] = useState<Detail>({ kind: 'idle' });
  const [groupId, setGroupId] = useState<string | null>(null);
  const [personId, setPersonId] = useState<string | null>(focusUser);
  const [crewQuery, setCrewQuery] = useState('');
  const [allCrew, setAllCrew] = useState(false);
  const [status, setStatus] = useState<StatusFilter>('all');
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState<CertificateSort | null>(null);
  const [page, setPage] = useState(1);
  const [tab, setTab] = useState<string | null>('matrix');
  const [panelTab, setPanelTab] = useState<string | null>('certificates');
  const [detailsOpen, setDetailsOpen] = useState(true);
  const tableRef = useRef<HTMLElement>(null);
  const detailsRef = useRef<HTMLElement>(null);
  // Belge dosyası (ADR 0164): önizlenen dosya, yükleme hedefi ve sonucu
  const [viewing, setViewing] = useState<CrewFileRecord | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const [uploadTarget, setUploadTarget] = useState<CertificateRecord | null>(null);
  const [upload, setUpload] = useState<
    { kind: 'busy' } | { kind: 'done' } | { kind: 'error'; code: string } | null
  >(null);
  // 1440 altında kişi başlığı sıkı (G2-15, G2-16)
  const compact = useMediaQuery('(max-width: 1439px)') ?? false;

  const load = useCallback(() => {
    setBase({ kind: 'loading' });
    Promise.all([source.vessels(), source.crew()])
      .then(([vessels, crew]) =>
        setBase({ kind: 'ready', today: crew.today, vessels, crew: crew.crew }),
      )
      .catch((error: unknown) => setBase({ kind: 'error', code: errorCode(error) }));
  }, [source]);
  useEffect(load, [load]);

  const ready = base.kind === 'ready' ? base : null;
  const groups = useMemo(
    () =>
      ready
        ? vesselGroups(ready.vessels, ready.crew, locale).filter(
            // Gemide karadakiler listelenmez: yalnız bu gemi
            (group) => source.scope === 'web' || group.id !== 'ashore',
          )
        : [],
    [ready, locale, source.scope],
  );
  // `?user=` ile gelen kişinin grubu; yoksa kişisi olan ilk gemi
  const focusMember = ready?.crew.find((member) => member.userId === focusUser) ?? null;
  const focusGroup = focusMember
    ? focusMember.status === 'ashore'
      ? 'ashore'
      : (focusMember.vessel?.id ?? null)
    : null;
  const activeGroup =
    groupId ??
    (focusGroup && groups.some((group) => group.id === focusGroup) ? focusGroup : null) ??
    groups.find((group) => group.count > 0)?.id ??
    groups[0]?.id ??
    null;
  // Sorgu değişince (ör. bildirimden başka kişi) o kişi seçilir
  useEffect(() => {
    if (!focusUser) return;
    setGroupId(null);
    setPersonId(focusUser);
    setPage(1);
  }, [focusUser]);
  const people = useMemo(
    () => (ready && activeGroup ? crewOfGroup(ready.crew, activeGroup, crewQuery, locale) : []),
    [ready, activeGroup, crewQuery, locale],
  );
  const groupPeople = useMemo(
    () => (ready && activeGroup ? crewOfGroup(ready.crew, activeGroup, '', locale) : []),
    [ready, activeGroup, locale],
  );
  const person = groupPeople.find((member) => member.userId === personId) ?? groupPeople[0] ?? null;

  const loadDetail = useCallback(
    (userId: string) => {
      setDetail({ kind: 'loading' });
      source
        .crewCertificates(userId)
        .then((data) => setDetail({ kind: 'ready', data }))
        .catch((error: unknown) => setDetail({ kind: 'error', code: errorCode(error) }));
    },
    [source],
  );
  const personKey = person?.userId ?? null;
  // biome-ignore lint/correctness/useExhaustiveDependencies: reloadKey yalnız yeniden okumayı tetikler
  useEffect(() => {
    if (personKey) loadDetail(personKey);
    else setDetail({ kind: 'idle' });
  }, [personKey, loadDetail, reloadKey]);

  const data = detail.kind === 'ready' ? detail.data : null;
  // Yazma yetkisi olmayan ofis rolünde (ör. DPA, ADR 0012 §2) ekleme ve düzeltme düğmeleri yok;
  // sunucu `canEdit: false` bildirir (ADR 0017 Ek 2026-10-29)
  const actions = data?.canEdit === false ? undefined : writeActions;
  const rows = useMemo(
    () => (data ? certificateRows(data, { status, query, locale, sort }) : []),
    [data, status, query, locale, sort],
  );
  const pages = Math.max(1, Math.ceil(rows.length / CERTIFICATE_PAGE_SIZE));
  const current = Math.min(page, pages);
  const visible = pageOf(rows, current, CERTIFICATE_PAGE_SIZE);
  const categories = useMemo(() => categoryCounts(data?.certificates ?? []), [data]);
  const summary = data?.summary;
  const missingCount = summary?.missing.length ?? 0;
  const total = summary ? summary.valid + summary.expiring + summary.expired : 0;
  const filtered = status !== 'all' || query.trim() !== '';

  const groupName = (id: string | null) =>
    id === 'ashore' ? t('certificates.ashore') : (groups.find((g) => g.id === id)?.name ?? '');
  const selectGroup = (id: string) => {
    setGroupId(id);
    setPersonId(null);
    setAllCrew(false);
    setPage(1);
  };
  const selectPerson = (id: string) => {
    setPersonId(id);
    setPage(1);
  };
  const clearFilters = () => {
    setStatus('all');
    setQuery('');
    setPage(1);
  };
  const categoryLabel = (row: CertificateRow) =>
    row.category
      ? t(`certificates.category.${categoryOf(row.category)}`)
      : t('certificates.required');
  const statusBadge = (value: RowStatus, withIcon = true) => {
    const Icon = STATUS_ICON[value];
    return (
      <StatusBadge tone={TONE[value]} icon={withIcon ? <Icon size={12} stroke={2.2} /> : undefined}>
        {t(`certificates.status.${value}`)}
      </StatusBadge>
    );
  };
  const personStatus = (member: CrewMember) => (
    <StatusBadge tone={member.status === 'on_board' ? 'success' : 'neutral'} outlined>
      {t(`crew.status.${member.status}`)}
    </StatusBadge>
  );
  const expiryNote = (row: CertificateRow) => {
    if (!row.record || !ready || row.status === 'valid') return null;
    if (row.record.expiresOn === null) return t('certificates.noExpiry');
    const left = daysLeft(row.record.expiresOn, ready.today) ?? 0;
    return left < 0
      ? t('certificates.daysAgo', { count: -left })
      : t('certificates.daysLeft', { count: left });
  };
  const ariaSort = (key: CertificateSortKey) =>
    sort?.key === key ? (sort.dir === 'asc' ? 'ascending' : 'descending') : undefined;
  const sortHeader = (key: CertificateSortKey, label: string) => {
    const active = sort?.key === key;
    const Icon = !active ? IconSelector : sort?.dir === 'asc' ? IconChevronUp : IconChevronDown;
    return (
      <UnstyledButton
        className={classes.sort}
        data-active={active || undefined}
        aria-label={t('certificates.sortBy', { column: label })}
        onClick={() => {
          setSort((previous) =>
            previous?.key === key
              ? { key, dir: previous.dir === 'asc' ? 'desc' : 'asc' }
              : { key, dir: 'asc' },
          );
          setPage(1);
        }}
      >
        <span>{label}</span>
        <Icon size={13} stroke={2} className={classes.sortIcon} aria-hidden />
      </UnstyledButton>
    );
  };

  const fileUrl = source.certificateFileUrl;
  const fileCell = (row: CertificateRow) => {
    const file = row.record?.file;
    if (!file || !fileUrl) {
      return (
        <>
          <span aria-hidden>—</span>
          <span className={classes.visuallyHidden}>{t('certificates.noFile')}</span>
        </>
      );
    }
    const versions = row.record?.fileVersions ?? 1;
    const title = [
      file.name,
      versions > 1 ? t('crewFiles.versions', { count: versions }) : null,
      file.onBoard === false ? t('crewFiles.notOnBoard') : null,
    ]
      .filter(Boolean)
      .join(' · ');
    return (
      <ActionIcon
        variant="subtle"
        {...(file.onBoard === false ? { color: 'gray' } : {})}
        aria-label={t('crewFiles.open', { name: file.name })}
        title={title}
        onClick={() => setViewing(file)}
      >
        {file.onBoard === false ? <IconCloudDownload size={16} /> : <IconFileText size={16} />}
      </ActionIcon>
    );
  };

  const startUpload = (record: CertificateRecord) => {
    setUploadTarget(record);
    fileInput.current?.click();
  };
  const onFileChosen = async (file: File | undefined) => {
    const target = uploadTarget;
    if (fileInput.current) fileInput.current.value = '';
    if (!file || !target || !person || !actions?.uploadFile) return;
    setUpload({ kind: 'busy' });
    try {
      await actions.uploadFile(person, target, file);
      setUpload({ kind: 'done' });
      loadDetail(person.userId);
    } catch (error) {
      setUpload({ kind: 'error', code: errorCode(error) });
    }
  };

  const rowActions = (row: CertificateRow) => {
    if (!actions || !person) return null;
    if (!row.record) {
      return (
        <Button
          size="compact-xs"
          variant="light"
          leftSection={<IconPlus size={14} />}
          onClick={() => actions.add(person, row.typeId)}
          aria-label={`${t('certificates.addMissing')}: ${row.name}`}
        >
          {t('certificates.addMissing')}
        </Button>
      );
    }
    const record = row.record;
    return (
      <Menu position="bottom-end" withinPortal>
        <Menu.Target>
          <ActionIcon
            variant="subtle"
            color="gray"
            aria-label={t('certificates.actions', { name: row.name })}
          >
            <IconDotsVertical size={16} />
          </ActionIcon>
        </Menu.Target>
        <Menu.Dropdown>
          <Menu.Item
            leftSection={<IconPencil size={16} />}
            onClick={() => actions.edit(person, record)}
          >
            {t('certificates.edit')}
          </Menu.Item>
          {actions.uploadFile ? (
            <Menu.Item
              leftSection={<IconUpload size={16} />}
              disabled={upload?.kind === 'busy'}
              onClick={() => startUpload(record)}
            >
              {record.file ? t('crewFiles.uploadNewVersion') : t('crewFiles.upload')}
            </Menu.Item>
          ) : null}
          <Menu.Item
            color="red"
            leftSection={<IconTrash size={16} />}
            onClick={() => actions.remove(person, record)}
          >
            {t('certificates.remove')}
          </Menu.Item>
        </Menu.Dropdown>
      </Menu>
    );
  };

  const stat = (key: string, label: string, value: number, icon: ReactNode, tone: string) => (
    <div key={key} className={classes.stat} data-tone={tone}>
      <span className={classes.statIcon} aria-hidden>
        {icon}
      </span>
      <span className={classes.statText}>
        <span className={classes.statLabel}>{label}</span>
        <span className={classes.statValue}>{value}</span>
      </span>
    </div>
  );

  const header = (
    <PageHeader title={t('certificates.title')} description={t('certificates.subtitle')} />
  );

  if (base.kind === 'error') {
    return (
      <div className={classes.main}>
        {header}
        <Panel>
          <EmptyState
            tone="danger"
            title={t('certificates.error')}
            description={t('common.loadError')}
            action={
              <Button size="xs" variant="light" onClick={load}>
                {t('common.retry')}
              </Button>
            }
          />
        </Panel>
      </div>
    );
  }

  const crewShown = allCrew || crewQuery ? people : people.slice(0, CREW_PREVIEW);
  const hiddenCrew = people.length - crewShown.length;
  const showDetails = detailsOpen && person !== null;
  const openDetails = () => {
    setDetailsOpen(true);
    // Sağ panel 1500 px altında içeriğin altında: oraya kayar ve başlığına odaklanır (G2-19)
    requestAnimationFrame(() => {
      detailsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      detailsRef.current?.querySelector<HTMLElement>('h2')?.focus({ preventScroll: true });
    });
  };

  const tableHead = (
    <thead>
      <tr>
        <th scope="col" className={classes.index}>
          #
        </th>
        <th scope="col" aria-sort={ariaSort('name')}>
          {sortHeader('name', t('certificates.columns.certificate'))}
        </th>
        <th scope="col" className={classes.categoryCol} aria-sort={ariaSort('category')}>
          {sortHeader('category', t('certificates.columns.category'))}
        </th>
        <th scope="col">{t('certificates.columns.number')}</th>
        <th scope="col" aria-sort={ariaSort('issued')}>
          {sortHeader('issued', t('certificates.columns.issued'))}
        </th>
        <th scope="col" aria-sort={ariaSort('expires')}>
          {sortHeader('expires', t('certificates.columns.expires'))}
        </th>
        <th scope="col" className={classes.statusCol} aria-sort={ariaSort('status')}>
          {sortHeader('status', t('certificates.columns.status'))}
        </th>
        <th scope="col">{t('certificates.columns.file')}</th>
        {actions ? (
          <th scope="col">
            <span className={classes.visuallyHidden}>{t('certificates.columns.actions')}</span>
          </th>
        ) : null}
      </tr>
    </thead>
  );

  const matrix =
    detail.kind === 'error' ? (
      <EmptyState
        tone="danger"
        title={t('certificates.error')}
        description={t('common.loadError')}
        action={
          <Button size="xs" variant="light" onClick={() => person && loadDetail(person.userId)}>
            {t('common.retry')}
          </Button>
        }
      />
    ) : data && rows.length === 0 ? (
      data.certificates.length + missingCount === 0 ? (
        <EmptyState
          icon={<IconFileOff size={22} stroke={1.8} />}
          title={t('certificates.emptyTitle')}
          description={actions ? t('certificates.emptyText') : t('certificates.emptyShip')}
        />
      ) : (
        <EmptyState
          icon={<IconFilterOff size={22} stroke={1.8} />}
          title={t('certificates.noMatch')}
          action={
            filtered ? (
              <Button size="xs" variant="light" onClick={clearFilters}>
                {t('common.clearFilters')}
              </Button>
            ) : null
          }
        />
      )
    ) : (
      <>
        {actions?.uploadFile ? (
          <input
            ref={fileInput}
            type="file"
            hidden
            accept={CREW_FILE_ACCEPT}
            aria-label={t('crewFiles.upload')}
            onChange={(event) => void onFileChosen(event.currentTarget.files?.[0])}
          />
        ) : null}
        {upload?.kind === 'error' ? (
          <p className={classes.uploadError} role="alert">
            {t(crewFileErrorKey(upload.code))}
          </p>
        ) : upload ? (
          <p className={classes.uploadStatus} role="status">
            {upload.kind === 'busy' ? t('crewFiles.uploading') : t('crewFiles.uploaded')}
          </p>
        ) : null}
        {fileUrl ? (
          <CrewFileViewer file={viewing} url={fileUrl} onClose={() => setViewing(null)} />
        ) : null}
        <section
          className={classes.tableWrap}
          ref={tableRef}
          aria-label={t('certificates.tabs.matrix')}
          aria-busy={data ? undefined : true}
          // Yatay kayan tablo klavyeyle de kaydırılabilsin (WCAG 2.1.1)
          // biome-ignore lint/a11y/noNoninteractiveTabindex: kaydırılabilir bölge
          tabIndex={0}
        >
          <table className={classes.table}>
            {tableHead}
            <tbody>
              {data
                ? visible.map((row, index) => {
                    const note = expiryNote(row);
                    return (
                      <tr key={row.key} data-status={row.status}>
                        <td className={classes.index}>
                          {(current - 1) * CERTIFICATE_PAGE_SIZE + index + 1}
                        </td>
                        <td className={classes.name}>
                          <span className={classes.nameText}>{row.name}</span>
                          <span className={classes.categoryLine}>{categoryLabel(row)}</span>
                        </td>
                        <td className={`${classes.secondary} ${classes.categoryCol}`}>
                          {categoryLabel(row)}
                        </td>
                        <td className={classes.number}>{row.record?.number ?? '—'}</td>
                        <td className={classes.date}>{formatDay(row.record?.issuedOn)}</td>
                        <td className={classes.date} title={note ?? undefined}>
                          {formatDay(row.record?.expiresOn)}
                          {note ? (
                            <span className={classes.visuallyHidden}>{` (${note})`}</span>
                          ) : null}
                        </td>
                        <td className={classes.statusCol}>{statusBadge(row.status)}</td>
                        <td className={classes.file}>{fileCell(row)}</td>
                        {actions ? <td className={classes.actions}>{rowActions(row)}</td> : null}
                      </tr>
                    );
                  })
                : Array.from({ length: SKELETON_ROWS }, (_, n) => (
                    // biome-ignore lint/suspicious/noArrayIndexKey: sabit iskelet satırları
                    <tr key={n} aria-hidden>
                      {Array.from({ length: actions ? 9 : 8 }, (_, m) => (
                        // biome-ignore lint/suspicious/noArrayIndexKey: sabit iskelet hücreleri
                        <td key={m} className={m === 2 ? classes.categoryCol : undefined}>
                          <Skeleton height={10} width={m === 1 ? '80%' : '60%'} />
                        </td>
                      ))}
                    </tr>
                  ))}
            </tbody>
          </table>
        </section>
        {data ? (
          <div className={classes.pager}>
            <span>
              {t('certificates.showing', {
                from: (current - 1) * CERTIFICATE_PAGE_SIZE + 1,
                to: Math.min(current * CERTIFICATE_PAGE_SIZE, rows.length),
                total: rows.length,
              })}
            </span>
            <Pagination
              total={pages}
              value={current}
              onChange={setPage}
              size="sm"
              aria-label={t('certificates.pagination')}
              getControlProps={(control) => ({
                'aria-label': t(`certificates.page.${control}`),
              })}
            />
          </div>
        ) : null}
      </>
    );

  const main = (
    <div className={classes.main}>
      {header}

      <section
        className={classes.filters}
        data-has-add={(actions && person) || undefined}
        aria-label={t('certificates.filters')}
      >
        <Select
          label={t('certificates.vessel')}
          data={groups.map((group) => ({
            value: group.id,
            label: group.name ?? t('certificates.ashore'),
          }))}
          value={activeGroup}
          allowDeselect={false}
          onChange={(value) => value && selectGroup(value)}
          disabled={!ready}
        />
        <Select
          label={t('certificates.crewMember')}
          data={[
            { value: '', label: t('certificates.allCrew') },
            ...groupPeople.map((member) => ({ value: member.userId, label: member.fullName })),
          ]}
          value={personId ?? ''}
          allowDeselect={false}
          onChange={(value) => (value ? selectPerson(value) : setPersonId(null))}
          disabled={!ready}
        />
        <TextInput
          label={t('certificates.certificate')}
          placeholder={t('certificates.search')}
          value={query}
          onChange={(event) => {
            setQuery(event.currentTarget.value);
            setPage(1);
          }}
          leftSection={<IconSearch size={16} aria-hidden />}
        />
        <Select
          label={t('certificates.filter.label')}
          data={(['all', 'valid', 'expiring', 'expired', 'missing'] as const).map((value) => ({
            value,
            label: t(`certificates.filter.${value}`),
          }))}
          value={status}
          allowDeselect={false}
          onChange={(value) => {
            setStatus((value ?? 'all') as StatusFilter);
            setPage(1);
          }}
        />
        {actions && person ? (
          <Button
            className={classes.add}
            leftSection={<IconPlus size={16} />}
            onClick={() => actions.add(person, null)}
          >
            {t('certificates.add')}
          </Button>
        ) : null}
      </section>

      <div className={classes.body}>
        <div className={classes.left}>
          <Panel title={t('certificates.vessels')} className={classes.vesselsPanel}>
            {ready ? (
              <ul className={classes.groups}>
                {groups.map((group) => {
                  const active = group.id === activeGroup;
                  return (
                    <li key={group.id}>
                      <UnstyledButton
                        className={classes.group}
                        data-active={active || undefined}
                        aria-pressed={active}
                        onClick={() => selectGroup(group.id)}
                      >
                        <span className={classes.groupIcon} aria-hidden>
                          {group.id === 'ashore' ? (
                            <IconUserFilled size={22} />
                          ) : (
                            <ShipGlyph size={30} />
                          )}
                        </span>
                        <span className={classes.groupText}>
                          <span className={classes.groupName}>
                            {group.name ?? t('certificates.ashore')}
                          </span>
                          <span className={classes.groupCount}>
                            {t('certificates.crewCount', { count: group.count })}
                          </span>
                        </span>
                        <IconChevronRight size={16} className={classes.chevron} aria-hidden />
                      </UnstyledButton>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <Skeleton height={52} />
            )}
          </Panel>

          <Panel
            className={classes.crewPanel}
            title={
              activeGroup === 'ashore'
                ? t('certificates.crewAshore')
                : t('certificates.crewOn', { vessel: groupName(activeGroup) })
            }
          >
            <TextInput
              placeholder={t('certificates.searchCrew')}
              aria-label={t('certificates.searchCrew')}
              leftSection={<IconSearch size={14} aria-hidden />}
              value={crewQuery}
              onChange={(event) => setCrewQuery(event.currentTarget.value)}
              mb={8}
            />
            {ready ? (
              people.length === 0 ? (
                <EmptyState
                  className={classes.compactEmpty}
                  icon={<IconUserSearch size={20} stroke={1.8} />}
                  title={crewQuery ? t('certificates.noCrewMatch') : t('certificates.noCrew')}
                />
              ) : (
                <ul className={classes.people}>
                  {crewShown.map((member) => {
                    const active = member.userId === person?.userId;
                    return (
                      <li key={member.userId}>
                        <UnstyledButton
                          className={classes.person}
                          data-active={active || undefined}
                          aria-pressed={active}
                          onClick={() => selectPerson(member.userId)}
                        >
                          <PersonAvatar name={member.fullName} size={40} />
                          <span className={classes.personText}>
                            <span className={classes.personName}>{member.fullName}</span>
                            <span className={classes.personRole}>{member.role ?? '—'}</span>
                          </span>
                          <IconChevronRight size={16} className={classes.chevron} aria-hidden />
                        </UnstyledButton>
                      </li>
                    );
                  })}
                </ul>
              )
            ) : (
              <div className={classes.peopleSkeleton} aria-hidden>
                {Array.from({ length: 5 }, (_, n) => (
                  // biome-ignore lint/suspicious/noArrayIndexKey: sabit iskelet satırları
                  <Skeleton key={n} height={40} />
                ))}
              </div>
            )}
            {hiddenCrew > 0 ? (
              <UnstyledButton className={classes.more} onClick={() => setAllCrew(true)}>
                <IconPlus size={14} aria-hidden />
                {t('certificates.moreCrew', { count: hiddenCrew })}
              </UnstyledButton>
            ) : null}
          </Panel>
        </div>

        <div className={classes.center}>
          <Panel flush className={classes.matrixPanel}>
            {ready && !person ? (
              <EmptyState
                icon={<IconUserSearch size={22} stroke={1.8} />}
                title={t('certificates.choosePerson')}
                description={groupPeople.length === 0 ? t('certificates.noCrewHint') : undefined}
              />
            ) : (
              <>
                <div className={classes.headWrap}>
                  {person ? (
                    <div className={classes.personHead}>
                      <PersonAvatar name={person.fullName} size={compact ? 56 : 72} />
                      <div className={classes.personHeadText}>
                        <div className={classes.nameRow}>
                          <h2 className={classes.personTitle}>{person.fullName}</h2>
                          {personStatus(person)}
                        </div>
                        <span className={classes.personSub}>
                          <span>{person.role ?? '—'}</span>
                          {person.vessel ? (
                            <>
                              <span className={classes.pipe} aria-hidden />
                              <span>{person.vessel.name}</span>
                            </>
                          ) : null}
                        </span>
                      </div>
                      <section
                        className={classes.stats}
                        aria-label={t('certificates.summary.label')}
                      >
                        {summary ? (
                          <>
                            {stat(
                              'total',
                              t('certificates.summary.total'),
                              total,
                              <IconFileText size={16} />,
                              'info',
                            )}
                            {stat(
                              'valid',
                              t('certificates.summary.valid'),
                              summary.valid,
                              <IconUsers size={16} />,
                              'success',
                            )}
                            {stat(
                              'expiring',
                              t('certificates.summary.expiring', { days: EXPIRING_DAYS }),
                              summary.expiring,
                              <IconHourglass size={16} />,
                              'warning',
                            )}
                            {stat(
                              'expired',
                              t('certificates.summary.expired'),
                              summary.expired,
                              <IconFileX size={16} />,
                              'danger',
                            )}
                            {stat(
                              'missing',
                              t('certificates.summary.missing'),
                              missingCount,
                              <IconCertificateOff size={16} />,
                              'neutral',
                            )}
                          </>
                        ) : (
                          <Skeleton height={40} width={480} />
                        )}
                      </section>
                    </div>
                  ) : (
                    <div className={classes.personHead} aria-hidden>
                      <Skeleton circle height={72} />
                      <Skeleton height={14} width={180} />
                    </div>
                  )}
                </div>

                <Tabs value={tab} onChange={setTab} className={classes.tabs}>
                  <div className={classes.tabsBar}>
                    <Tabs.List aria-label={t('certificates.tabs.label')}>
                      <Tabs.Tab value="matrix">{t('certificates.tabs.matrix')}</Tabs.Tab>
                      <Tabs.Tab value="documents">{t('certificates.tabs.documents')}</Tabs.Tab>
                      <Tabs.Tab value="employment">{t('certificates.tabs.employment')}</Tabs.Tab>
                      <Tabs.Tab value="notes">{t('certificates.tabs.notes')}</Tabs.Tab>
                    </Tabs.List>
                    {person ? (
                      // Sağ panel yanda değilken (1500 px altı ya da kapalı) ona götürür (G2-19)
                      <Button
                        className={classes.detailsButton}
                        size="compact-sm"
                        variant="subtle"
                        leftSection={<IconUserCircle size={16} />}
                        onClick={openDetails}
                      >
                        {t('certificates.details.open')}
                      </Button>
                    ) : null}
                  </div>

                  <Tabs.Panel value="matrix">{matrix}</Tabs.Panel>
                  <Tabs.Panel value="documents">
                    <EmptyState
                      icon={<IconFileDescription size={22} stroke={1.8} />}
                      title={t('certificates.documentsTitle')}
                      description={t('certificates.documentsEmpty')}
                    />
                  </Tabs.Panel>
                  <Tabs.Panel value="employment">
                    {person ? <Employment person={person} /> : null}
                  </Tabs.Panel>
                  <Tabs.Panel value="notes">
                    <EmptyState title={t('certificates.notesEmpty')} />
                  </Tabs.Panel>
                </Tabs>
              </>
            )}
          </Panel>

          {summary && person ? (
            <div className={classes.bottom}>
              <CertificateOverview
                size={108}
                certificates={{
                  valid: summary.valid,
                  expiring: summary.expiring,
                  expired: summary.expired,
                  missing: missingCount,
                }}
              />
              <Panel title={t('certificates.categories')}>
                <div className={classes.categoriesCard}>
                  <ul className={classes.categories}>
                    {CERTIFICATE_CATEGORIES.map((category) => {
                      const Icon = CATEGORY_ICON[category];
                      const name = t(`certificates.category.${category}`);
                      return (
                        <li
                          key={category}
                          className={classes.categoryTile}
                          data-category={category}
                        >
                          <span className={classes.categoryIcon} aria-hidden>
                            <Icon size={15} />
                          </span>
                          <span className={classes.categoryText}>
                            <span title={name}>{name}</span>
                            <strong>{categories[category]}</strong>
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </Panel>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );

  return (
    <div className={classes.screen} data-details={showDetails || undefined}>
      {main}
      {showDetails && person ? (
        <aside
          className={classes.details}
          aria-label={t('certificates.details.title')}
          ref={detailsRef}
        >
          <div className={classes.detailsHead}>
            <h2 className={classes.detailsTitle} tabIndex={-1}>
              {t('certificates.details.title')}
            </h2>
            <CloseButton
              aria-label={t('certificates.details.close')}
              onClick={() => setDetailsOpen(false)}
            />
          </div>
          <div className={classes.profile}>
            <PersonAvatar name={person.fullName} size={88} />
            <div className={classes.profileText}>
              <div className={classes.nameRow}>
                <span className={classes.profileName}>{person.fullName}</span>
                {personStatus(person)}
              </div>
              <span className={classes.profileRole}>{person.role ?? '—'}</span>
              <span className={classes.profileVessel}>{person.vessel?.name ?? '—'}</span>
            </div>
          </div>
          <Employment person={person} compact />

          <Tabs value={panelTab} onChange={setPanelTab} className={classes.panelTabs}>
            <Tabs.List grow>
              <Tabs.Tab value="certificates">{t('certificates.details.certificates')}</Tabs.Tab>
              <Tabs.Tab value="documents">{t('certificates.details.documents')}</Tabs.Tab>
              <Tabs.Tab value="employment">{t('certificates.details.employment')}</Tabs.Tab>
            </Tabs.List>
            <Tabs.Panel value="certificates">
              <h3 className={classes.detailsList}>{t('certificates.details.list')}</h3>
              {data ? (
                data.certificates.length === 0 ? (
                  <EmptyState
                    className={classes.compactEmpty}
                    icon={<IconFileOff size={20} stroke={1.8} />}
                    title={t('certificates.emptyTitle')}
                  />
                ) : (
                  <ul className={classes.cards}>
                    {certificateRows(data, { status: 'all', query: '', locale })
                      .filter((row) => row.record)
                      .slice(0, DETAILS_PREVIEW)
                      .map((row) => {
                        const Icon = CATEGORY_ICON[categoryOf(row.category)];
                        return (
                          <li key={row.key} className={classes.card} data-status={row.status}>
                            <span className={classes.cardIcon} aria-hidden>
                              <Icon size={16} />
                            </span>
                            <span className={classes.cardText}>
                              <span className={classes.cardName} title={row.name}>
                                {row.name}
                              </span>
                              <span className={classes.cardNumber}>
                                {row.record?.number ?? '—'}
                              </span>
                            </span>
                            {statusBadge(row.status, row.status !== 'valid')}
                            <span className={classes.cardDate}>
                              {formatDay(row.record?.expiresOn)}
                            </span>
                          </li>
                        );
                      })}
                  </ul>
                )
              ) : (
                <div className={classes.peopleSkeleton} aria-hidden>
                  {Array.from({ length: 4 }, (_, n) => (
                    // biome-ignore lint/suspicious/noArrayIndexKey: sabit iskelet satırları
                    <Skeleton key={n} height={44} />
                  ))}
                </div>
              )}
              {data && data.certificates.length > 0 ? (
                <UnstyledButton
                  className={classes.showAll}
                  onClick={() => {
                    setTab('matrix');
                    setStatus('all');
                    setQuery('');
                    tableRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }}
                >
                  {t('certificates.details.showAll', { count: data.certificates.length })}
                </UnstyledButton>
              ) : null}
            </Tabs.Panel>
            <Tabs.Panel value="documents">
              <EmptyState
                className={classes.compactEmpty}
                title={t('certificates.documentsTitle')}
                description={t('certificates.documentsEmpty')}
              />
            </Tabs.Panel>
            <Tabs.Panel value="employment">
              <Employment person={person} />
            </Tabs.Panel>
          </Tabs>

          {summary ? (
            <div className={classes.notice} role="status">
              <IconInfoCircleFilled size={22} className={classes.noticeIcon} aria-hidden />
              <div>
                {summary.expiring + summary.expired + missingCount === 0 ? (
                  <p>{t('certificates.alertNone')}</p>
                ) : (
                  <>
                    {summary.expiring > 0 ? (
                      <p>
                        {t('certificates.alertExpiring', {
                          count: summary.expiring,
                          days: EXPIRING_DAYS,
                        })}
                      </p>
                    ) : null}
                    {summary.expired > 0 ? (
                      <p>{t('certificates.alertExpired', { count: summary.expired })}</p>
                    ) : null}
                    {missingCount > 0 ? (
                      <p>{t('certificates.alertMissing', { count: missingCount })}</p>
                    ) : null}
                    <p className={classes.noticeAction}>{t('certificates.alertAction')}</p>
                  </>
                )}
              </div>
            </div>
          ) : null}
        </aside>
      ) : null}
    </div>
  );
}

/** Kişinin görev bilgisi (atamadan): gemi, rütbe, katılış, ayrılış ya da sonraki görev. */
function Employment({ person, compact = false }: { person: CrewMember; compact?: boolean }) {
  const { t } = useTranslation();
  const onBoard = person.status === 'on_board';
  const rows: [string, string][] = [
    ...(compact
      ? []
      : ([
          [t('certificates.employment.vessel'), person.vessel?.name ?? '—'],
          [t('certificates.employment.rank'), person.role ?? '—'],
        ] as [string, string][])),
    [t('certificates.employment.signOn'), onBoard ? formatDay(person.since) : '—'],
    [t('certificates.employment.signOff'), onBoard ? '—' : formatDay(person.since)],
    [
      onBoard ? t('certificates.employment.days') : t('certificates.employment.daysAshore'),
      person.days === null ? '—' : String(person.days),
    ],
    ...(person.next
      ? ([
          [
            t('certificates.employment.next'),
            `${person.next.vessel?.name ?? '—'} · ${formatDay(person.next.from)}`,
          ],
        ] as [string, string][])
      : []),
  ];
  return (
    <dl className={compact ? classes.factsCompact : classes.facts}>
      {rows.map(([label, value]) => (
        <div key={label} className={classes.fact}>
          <dt>{label}</dt>
          <dd>{value}</dd>
        </div>
      ))}
    </dl>
  );
}
